import React from 'react';
import { Loader2, CheckSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { useI18n } from '../contexts/I18nContext';

/**
 * Modal for bulk-updating multiple shipments at once. Stateless — owner controls
 * dialog open state and submission. Empty values mean "leave unchanged".
 */
export function BulkEditDialog({
  open,
  onOpenChange,
  selectedCount,
  bulkEditData,
  setBulkEditData,
  statuses,
  clients,
  importers,
  bulkUpdating,
  onConfirm,
}) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('shipments.bulkEdit')}</DialogTitle>
          <DialogDescription>
            {t('shipments.bulkEditDescription', { count: selectedCount })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>{t('shipments.status')}</Label>
            <select
              className="mt-1 w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={bulkEditData.status_general_code}
              onChange={(e) => setBulkEditData({ ...bulkEditData, status_general_code: e.target.value })}
              data-testid="bulk-status-select"
            >
              <option value="">{t('shipments.noChange')}</option>
              {statuses.map(s => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t('shipments.client')}</Label>
            <select
              className="mt-1 w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={bulkEditData.client_id}
              onChange={(e) => setBulkEditData({ ...bulkEditData, client_id: e.target.value })}
              data-testid="bulk-client-select"
            >
              <option value="">{t('shipments.noChange')}</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t('shipments.importer')}</Label>
            <select
              className="mt-1 w-full h-10 px-3 py-2 bg-background border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              value={bulkEditData.importer_id}
              onChange={(e) => setBulkEditData({ ...bulkEditData, importer_id: e.target.value })}
              data-testid="bulk-importer-select"
            >
              <option value="">{t('shipments.noChange')}</option>
              {importers.map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={bulkUpdating}>
            {t('shipments.cancel')}
          </Button>
          <Button onClick={onConfirm} disabled={bulkUpdating} data-testid="confirm-bulk-edit">
            {bulkUpdating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckSquare size={16} className="mr-2" />}
            {t('shipments.update')} {selectedCount} {t('shipments.countSuffix')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
